import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router";

import {
  creditOf,
  dividedBy,
  ZERO,
  isNegative,
  isPositive,
  moneyForInput,
  nearestUnitsFor,
  readMoney,
  saleTotals,
  sameMoney,
  type Money,
  type SaleTotals,
  type TillStock,
} from "@pharmacy/domain";

import { newId } from "@/utils/ids";
import type { Sale, TillProduct } from "./api";

/**
 * The sale being put together on Add sale, or the sale being edited, until it
 * is finished or thrown away (apps/mobile/src/sales/draft.tsx).
 *
 * Held above every page (in the app frame), so going to Products in the
 * middle of a sale loses nothing: Sales then offers "Continue sale · $total".
 * It lives in this tab's memory only; while there is one, closing or
 * reloading the tab asks the browser's "Leave site?" (guide 8.1).
 */

/**
 * One medicine box on Add sale: a medicine, and how many of each of its
 * packs — 1 box, 2 strips and 5 tablets in one box (2026-10-18). Each pack
 * with a number becomes its own line of the sale.
 */
export interface Box {
  /** This box on this page. Not a server id. */
  key: string;
  /** Null until a medicine is picked. */
  product: TillProduct | null;
  /** How many of each size, as typed, by size id. Empty or missing is none. */
  qtys: Record<string, string>;
  /** A changed price as typed, by size id. Missing is the size's normal price. */
  prices: Record<string, string>;
  /**
   * A shop's sale by money, as typed: "$1 of sugar" (DukaanBook, 2026-10-27).
   * Sold in the smallest unit, the nearest whole number of it the money buys.
   */
  money?: string;
}

/** A sale being corrected rather than a new one. */
export interface Editing {
  saleId: string;
  receiptNo: string;
  payments: Sale["payments"];
  /**
   * What the sale already took, per medicine, in the smallest unit. It goes
   * back on the shelf when the edit is saved, so it counts as available while
   * editing: 5 of 5 sold, edited to 4, is not "none left".
   */
  takenBase: Record<string, number>;
  sale: Sale;
}

/** One size of a box, ready to sell: medicine, size, number and price all good. */
export interface ReadyLine {
  box: Box;
  /** The box and its size: one box makes a line for each size with a number. */
  key: string;
  product: TillProduct;
  unitId: string;
  multiplier: number;
  qty: number;
  normalPrice: Money;
  price: Money;
  /** A sale by money: the money asked, which is the line's total. */
  moneyAsked?: Money;
  total?: Money;
}

interface DraftValue {
  /** The id the sale (or the corrected version) will be recorded under. Made once. */
  saleId: string;
  editing: Editing | null;
  customerName: string;
  /** The nine digits after +252, as the phone box holds them. */
  customerPhone: string;
  boxes: Box[];
  /** Anything worth keeping has been entered: offer "Continue sale". */
  started: boolean;
  lines: ReadyLine[];
  /** A box with a medicine whose number or price is not usable yet. */
  unfinished: boolean;
  totals: SaleTotals;
  /**
   * How much of it is on credit (deyn), as typed in its box on the page that
   * checks the sale. Empty is none; what is not on credit is paid now.
   */
  creditText: string;
  /** Taken off the whole sale, as typed. Empty is none. */
  discountText: string;
  /** That discount read: none is zero, and null while what is typed is not an amount. */
  saleDiscount: Money | null;

  setCustomerName: (value: string) => void;
  setCustomerPhone: (value: string) => void;
  setCreditText: (value: string) => void;
  setDiscountText: (value: string) => void;
  /** Returns the new box's key, so the page can put the cursor in it. */
  addBox: () => string;
  removeBox: (key: string) => void;
  updateBox: (key: string, change: Partial<Omit<Box, "key">>) => void;
  /** Everything of this medicine on the page, in the smallest unit. */
  needBase: (productId: string) => number;
  /** What there is to sell from, counting back what an edit returns. */
  stockOf: (product: TillProduct) => TillStock;
  startEdit: (sale: Sale, products: TillProduct[]) => void;
  /** The sale is recorded: an empty page, and its receipt to show. */
  finish: (sale: Sale) => void;
  /** Thrown away. */
  clear: () => void;
  lastSale: Sale | null;
}

const DraftContext = createContext<DraftValue | null>(null);

const emptyBox = (): Box => ({ key: newId(), product: null, qtys: {}, prices: {} });

export function sizeIn(product: TillProduct, unitId: string | null) {
  return product.sizes.find((size) => size.id === unitId) ?? product.sizes[0]!;
}

/** What one of this size is charged in the box: the normal price, or the changed one. */
export function chargedPrice(box: Box, unitId: string): Money | null {
  if (!box.product) return null;
  const typed = box.prices[unitId];
  return typed === undefined ? (sizeIn(box.product, unitId).sellPrice as Money) : readMoney(typed);
}

/**
 * A line of a sale as the box will hold it: its own size, or — when that size
 * has been taken off the medicine since — the smallest unit, with the number
 * and price worked out from the count in the size's name ("box (100)").
 */
function asSold(product: TillProduct, unitId: string, sizeName: string, qty: number, price: Money) {
  const size = product.sizes.find((s) => s.id === unitId);
  if (size) return { size, qty, price };
  const smallest = [...product.sizes].sort((a, b) => a.multiplier - b.multiplier)[0]!;
  const count = Number(/\((\d+)\)$/.exec(sizeName)?.[1] ?? "1");
  return { size: smallest, qty: qty * count, price: dividedBy(price, count) };
}

/** How many of this size are in the box: 0 for an empty number. */
export function countIn(box: Box, unitId: string): number {
  const typed = box.qtys[unitId] ?? "";
  return typed === "" ? 0 : Number(typed);
}

/**
 * A box's sale by money, once it can be read: the money, the smallest unit it
 * is sold in, and how many of that unit it buys. Null when there is none, and
 * "unusable" while the amount is not one or the unit has no price to share by.
 */
export function moneyIn(box: Box) {
  const typed = box.money?.trim() ?? "";
  if (!box.product || typed === "") return null;
  const unit = [...box.product.sizes].sort((a, b) => a.multiplier - b.multiplier)[0]!;
  const amount = readMoney(typed);
  const price = unit.sellPrice as Money;
  // Nothing by money, as a pack with no number is nothing of that pack.
  if (amount !== null && !isNegative(amount) && !isPositive(amount)) return null;
  if (amount === null || isNegative(amount) || unit.multiplier !== 1 || !isPositive(price)) return "unusable" as const;
  return { amount, unit, price, qty: nearestUnitsFor(amount, price) };
}

/** The box's sizes, biggest first: how a shelf is counted, and how the boxes ask. */
export function sizesOf(product: TillProduct) {
  return [...product.sizes].sort((a, b) => b.multiplier - a.multiplier);
}

export function DraftProvider({ children }: { children: React.ReactNode }) {
  const [saleId, setSaleId] = useState(newId);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [creditText, setCreditText] = useState("");
  const [discountText, setDiscountText] = useState("");
  const [boxes, setBoxes] = useState<Box[]>(() => [emptyBox()]);
  const [lastSale, setLastSale] = useState<Sale | null>(null);

  const reset = useCallback(() => {
    setSaleId(newId());
    setEditing(null);
    setCustomerName("");
    setCustomerPhone("");
    setCreditText("");
    setDiscountText("");
    setBoxes([emptyBox()]);
  }, []);

  const addBox = useCallback(() => {
    const box = emptyBox();
    setBoxes((current) => [...current, box]);
    return box.key;
  }, []);

  const removeBox = useCallback((key: string) => {
    setBoxes((current) => {
      const rest = current.filter((box) => box.key !== key);
      // There is always somewhere to pick the next medicine.
      return rest.length > 0 ? rest : [emptyBox()];
    });
  }, []);

  const updateBox = useCallback((key: string, change: Partial<Omit<Box, "key">>) => {
    setBoxes((current) => current.map((box) => (box.key === key ? { ...box, ...change } : box)));
  }, []);

  const startEdit = useCallback((sale: Sale, products: TillProduct[]) => {
    const byId = new Map(products.map((product) => [product.id, product]));
    const takenBase: Record<string, number> = {};
    const edited: Box[] = [];
    const boxOf = new Map<string, Box>();
    for (const line of sale.lines) {
      const product = byId.get(line.productId);
      if (!product) continue;
      // A sale by money stays one: the money asked, in its product's box.
      if (line.moneyAsked) {
        takenBase[product.id] = (takenBase[product.id] ?? 0) + line.qty;
        let box = boxOf.get(product.id);
        if (!box || box.money !== undefined) {
          box = { key: newId(), product, qtys: {}, prices: {} };
          boxOf.set(product.id, box);
          edited.push(box);
        }
        box.money = moneyForInput(line.moneyAsked as Money);
        continue;
      }
      // Its size taken off the medicine since: back in the smallest unit, so
      // the edit keeps the same amount — 2 boxes of 100 are 200 tablets, not 2.
      const sold = asSold(product, line.unitId, line.sizeName, line.qty, line.price as Money);
      const size = sold.size;
      takenBase[product.id] = (takenBase[product.id] ?? 0) + sold.qty * size.multiplier;
      // One box per medicine, its sizes together — unless a size is in it twice, at two prices.
      let box = boxOf.get(product.id);
      if (!box || box.qtys[size.id] !== undefined) {
        box = { key: newId(), product, qtys: {}, prices: {} };
        boxOf.set(product.id, box);
        edited.push(box);
      }
      box.qtys[size.id] = String(sold.qty);
      // Anything other than today's normal price stays as it was charged.
      if (!sameMoney(sold.price, size.sellPrice as Money)) box.prices[size.id] = moneyForInput(sold.price);
    }
    setSaleId(newId());
    setEditing({ saleId: sale.id, receiptNo: sale.receiptNo, payments: sale.payments, takenBase, sale });
    setCustomerName(sale.customerName ?? "");
    setCustomerPhone((sale.customerPhone ?? "").replace(/^\+252/, "").replace(/\D/g, ""));
    // Credit as it was: how much of it was owed.
    const owed = creditOf(sale.payments as Array<{ method: string; amount: Money }>);
    setCreditText(isPositive(owed) ? moneyForInput(owed) : "");
    const given = sale.saleDiscount as Money | undefined;
    setDiscountText(given && isPositive(given) ? moneyForInput(given) : "");
    setBoxes(edited.length > 0 ? edited : [emptyBox()]);
  }, []);

  const finish = useCallback(
    (sale: Sale) => {
      setLastSale(sale);
      reset();
    },
    [reset],
  );

  const value = useMemo<DraftValue>(() => {
    const lines: ReadyLine[] = [];
    let unfinished = false;
    for (const box of boxes) {
      if (!box.product) continue;
      let any = false;
      for (const size of sizesOf(box.product)) {
        const qty = countIn(box, size.id);
        if (qty === 0) continue;
        any = true;
        const price = chargedPrice(box, size.id);
        if (qty < 1 || !Number.isInteger(qty) || price === null || isNegative(price)) {
          unfinished = true;
          continue;
        }
        lines.push({
          box,
          key: `${box.key}:${size.id}`,
          product: box.product,
          unitId: size.id,
          multiplier: size.multiplier,
          qty,
          normalPrice: size.sellPrice as Money,
          price,
        });
      }
      const byMoney = moneyIn(box);
      if (byMoney === "unusable") {
        any = true;
        unfinished = true;
      } else if (byMoney) {
        any = true;
        lines.push({
          box,
          key: `${box.key}:money`,
          product: box.product,
          unitId: byMoney.unit.id,
          multiplier: 1,
          qty: byMoney.qty,
          normalPrice: byMoney.price,
          price: byMoney.price,
          moneyAsked: byMoney.amount,
          total: byMoney.amount,
        });
      }
      // A medicine with no number at all is not sold yet.
      if (!any) unfinished = true;
    }

    const needBase = (productId: string) =>
      boxes.reduce((sum, box) => {
        if (box.product?.id !== productId) return sum;
        const byMoney = moneyIn(box);
        const fromMoney = byMoney && byMoney !== "unusable" ? byMoney.qty : 0;
        return (
          sum +
          fromMoney +
          box.product.sizes.reduce((inBox, size) => inBox + countIn(box, size.id) * size.multiplier, 0)
        );
      }, 0);

    const saleDiscount = discountText.trim() === "" ? ZERO : readMoney(discountText);

    const stockOf = (product: TillProduct): TillStock => {
      const back = editing?.takenBase[product.id] ?? 0;
      return {
        qtyBase: product.stock.qtyBase + back,
        sellableBase: product.stock.sellableBase + back,
        expiredBase: product.stock.expiredBase,
      };
    };

    return {
      saleId,
      editing,
      customerName,
      customerPhone,
      creditText,
      discountText,
      saleDiscount,
      boxes,
      started:
        editing !== null ||
        customerName.trim() !== "" ||
        customerPhone !== "" ||
        boxes.some((box) => box.product !== null),
      lines,
      unfinished,
      // A discount not yet readable counts as none until it is.
      totals: saleTotals(lines, saleDiscount !== null && !isNegative(saleDiscount) ? saleDiscount : ZERO),
      setCustomerName,
      setCustomerPhone,
      setCreditText,
      setDiscountText,
      addBox,
      removeBox,
      updateBox,
      needBase,
      stockOf,
      startEdit,
      finish,
      clear: reset,
      lastSale,
    };
  }, [
    saleId,
    editing,
    customerName,
    customerPhone,
    creditText,
    discountText,
    boxes,
    lastSale,
    addBox,
    removeBox,
    updateBox,
    startEdit,
    finish,
    reset,
  ]);

  // An unfinished sale lives only in this tab: closing or reloading it asks first.
  useEffect(() => {
    if (!value.started) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [value.started]);

  return <DraftContext.Provider value={value}>{children}</DraftContext.Provider>;
}

export function useDraft(): DraftValue {
  const value = useContext(DraftContext);
  if (!value) throw new Error("useDraft must be used inside a DraftProvider");
  return value;
}

/** The draft around every signed-in page (router.tsx): going elsewhere mid-sale loses nothing. */
export function DraftLayout() {
  return (
    <DraftProvider>
      <Outlet />
    </DraftProvider>
  );
}
